/**
 * Local hand-rolled REST client — retained for:
 *   - /chat dispatch+poll (useChatDispatch / usePollJobResult) — correct-by-design
 *   - TanStack Query hooks that call stable same-origin BFF routes (`/admin/*`, `/v1/*`, etc.)
 *
 * Convention: prefer this `api` client for **REST/BFF** contracts (JWT + `/admin/*`)
 * whenever a stable HTTP route exists. Use `getAgentHubClient().callTool` only for
 * surfaces that are still tool-only and have no first-party REST mapping yet.
 */
const API_BASE =
  import.meta.env.VITE_API_URL || "https://agent-hub.pulsebusiness.ai";

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

class ApiClient {
  private baseUrl: string;
  private tokenGetter: (() => string | null) | null = null;
  private tokenRefresher: (() => Promise<void>) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setTokenGetter(getter: () => string | null) {
    this.tokenGetter = getter;
  }

  setTokenRefresher(refresher: () => Promise<void>) {
    this.tokenRefresher = refresher;
  }

  private async request<T>(
    path: string,
    options: RequestOptions = {},
    isRetry = false,
  ): Promise<{ data: T; status: number }> {
    const { method = "GET", body, headers = {}, signal } = options;

    const token = this.tokenGetter?.();
    const requestHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      ...headers,
    };

    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }

    const url = path.startsWith("/api/") ? path : `${this.baseUrl}${path}`;
    const response = await globalThis.fetch(url, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      signal,
      credentials: "include",
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({
        error: response.statusText,
      }));
      const errorMessage =
        (errorBody as Record<string, string>).error ||
        (errorBody as Record<string, string>).message ||
        (errorBody as Record<string, string>).detail ||
        `HTTP ${response.status}`;
      const unauthorized = response.status === 401;
      const looksLikeTokenExpiry =
        typeof errorMessage === "string" &&
        /(jwt expired|token expired|invalid token)/i.test(errorMessage);

      if (
        !isRetry &&
        this.tokenRefresher &&
        (unauthorized || looksLikeTokenExpiry)
      ) {
        try {
          await this.tokenRefresher();
          return this.request<T>(path, options, true);
        } catch {
          /* Refresh failed -- fall through */
        }
      }

      const error = new Error(errorMessage || `HTTP ${response.status}`);
      (error as Error & { status: number }).status = response.status;
      throw error;
    }

    const data = (await response.json()) as T;
    return { data, status: response.status };
  }

  get<T>(path: string, options?: Omit<RequestOptions, "method" | "body">) {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  post<T>(path: string, options?: Omit<RequestOptions, "method">) {
    return this.request<T>(path, { ...options, method: "POST" });
  }

  put<T>(path: string, options?: Omit<RequestOptions, "method">) {
    return this.request<T>(path, { ...options, method: "PUT" });
  }

  patch<T>(path: string, options?: Omit<RequestOptions, "method">) {
    return this.request<T>(path, { ...options, method: "PATCH" });
  }

  delete<T>(path: string, options?: Omit<RequestOptions, "method" | "body">) {
    return this.request<T>(path, { ...options, method: "DELETE" });
  }
}

export const api = new ApiClient(API_BASE);
